import React, { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Box, Fab, Grid, List, TextField, Typography } from "@mui/material";
import { intersection } from "lodash";
import { useFormContext } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import AllergenIngredientModal from "@/components/AllergenModal/AllergenIngredientModal";
import FoodSelect from "@/components/FoodSelect";
import { AllergiesKind, FoodItemFragment } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { isBlank } from "@/utils/any";
import { useUpdateableFieldArray } from "@/utils/hookForm";

import IngredientsListItem from "./IngredientListItem";
import { RecipeFormValues } from "./RecipeForm";

const useStyles = makeStyles()({
  list: {
    flex: 1,
    height: "40vh",
    overflowY: "auto",
    overflowX: "hidden",
  },
  label: {
    fontWeight: 600,
    fontSize: 14,
    color: colors.grey,
  },
});

const Root = withStyles(Box, () => ({
  root: {
    flex: 1,
  },
}));

const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.white,
      "& input::-webkit-clear-button ": {
        display: "none",
      },
      "& input::-webkit-outer-spin-button": {
        display: "none",
      },
      "& input::-webkit-inner-spin-button": {
        display: "none",
      },
    },
  },
});

const IngredientsForm: React.FC = () => {
  const { classes } = useStyles();
  const { fields, append, update, remove } = useUpdateableFieldArray<
    RecipeFormValues,
    "ingredients"
  >({
    name: "ingredients",
  });

  const urlParams = new URLSearchParams(window.location.search);
  const queryParams = urlParams.get("allergies") ?? "";
  const clientAllergies = queryParams.length
    ? (queryParams.split(",") as AllergiesKind[])
    : [];

  const { setValue, getValues } = useFormContext<RecipeFormValues>();
  const [selectedIngred, setSelectedIngred] = useState<FoodItemFragment>();
  const [amount, setAmount] = useState<number>(0);
  const [portionId, setPortionId] = useState<string>("");

  const disabled =
    isBlank(selectedIngred) || isBlank(amount) || isBlank(portionId);

  useEffect(() => {
    if (portionId && amount) {
      setValue("selectedIngredient.amount", amount);
      setValue("selectedIngredient.food", selectedIngred);
      setValue(
        "selectedIngredient.measurement",
        selectedIngred?.portions.find((p) => p.id === portionId)
      );
    }
  }, [portionId, amount]);

  const addIngredient = () => {
    if (selectedIngred === undefined) return;

    const existingIngredientIndex = fields.findIndex(
      (i) => i.food.id === selectedIngred.id
    );
    if (existingIngredientIndex !== -1) {
      const existingIngredient = fields[existingIngredientIndex];
      if (existingIngredient.foodPortion.id === portionId) {
        update(existingIngredientIndex, {
          ...existingIngredient,
          amount: existingIngredient.amount + amount,
        });
      } else {
        update(existingIngredientIndex, {
          ...existingIngredient,
          foodPortion: selectedIngred.portions.find((p) => p.id === portionId)!,
          amount: amount,
        });
      }
    } else {
      const newIngredient = {
        id: undefined,
        food: selectedIngred, // store entire food object for computing macronutrients, not saved
        foodPortion: selectedIngred.portions.find((p) => p.id === portionId)!,
        amount: amount,
        _destroy: false,
      };
      append(newIngredient);
    }
    setSelectedIngred(undefined);
    setAmount(0);
    setPortionId("");
    setValue("selectedIngredient.amount", 0);
    setValue("selectedIngredient.food", undefined);
    setValue("selectedIngredient.measurement", undefined);
  };
  const [allergenIngredientModal, setAllergenIngredientModal] = useState(false);

  const handleAdd = () => {
    const hasAllergies =
      intersection(selectedIngred?.allergies, clientAllergies).length !== 0;
    if (hasAllergies) {
      setAllergenIngredientModal(true);
    } else {
      addIngredient();
    }
  };

  return (
    <Root>
      <Box
        display="flex"
        marginTop={4}
        alignItems="flex-end"
        position="relative"
        flex={1}
      >
        <Grid container spacing={2}>
          <Grid item xs zeroMinWidth>
            <Typography className={classes.label}>Ingredient</Typography>
            <Box marginBottom={3} />
            <FoodSelect
              placeholder="Select ingredient"
              value={selectedIngred}
              onChange={setSelectedIngred}
              clientAllergies={clientAllergies}
            />
          </Grid>
          <Grid item xs={2}>
            <Typography className={classes.label}>Qty</Typography>
            <Box marginBottom={3} />
            <StyledTextField
              fullWidth
              size="small"
              type="number"
              name="amount"
              value={amount}
              onChange={(event) => {
                setAmount(parseFloat(event.target.value));
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <Typography className={classes.label}>Measurement</Typography>
            <Box marginBottom={3} />
            <StyledTextField
              fullWidth
              select
              size="small"
              name="portionIndex"
              value={portionId}
              onChange={(event) => {
                setPortionId(event.target.value);
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option disabled value="">
                None
              </option>
              {selectedIngred?.portions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.unit}
                </option>
              ))}
            </StyledTextField>
          </Grid>
          <Grid item>
            <Box marginTop={7}>
              <Fab
                color="primary"
                size="small"
                disabled={disabled}
                onClick={handleAdd}
              >
                <AddIcon />
              </Fab>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <List className={classes.list} disablePadding>
        {fields.map((i, index) => (
          <IngredientsListItem
            key={i.key}
            ingredient={i}
            onRemoveIngredient={() => remove(index)}
          />
        ))}
      </List>
      <AllergenIngredientModal
        foodId={selectedIngred ? selectedIngred?.id : ""}
        visibility={allergenIngredientModal}
        onAddIngredient={() => {
          setAllergenIngredientModal(false);
          addIngredient();
        }}
        onClose={() => setAllergenIngredientModal(false)}
      />
    </Root>
  );
};

export default IngredientsForm;
